/* Subscription commitment dialog for the Appstle Subscriptions widget.
 *
 * Every user activation of a subscription plan radio opens a modal that
 * requires explicit agreement to the three-order minimum commitment. If the
 * shopper declines (button, Esc, or backdrop click), the widget is reverted
 * to the one-time purchase option. A capture-phase submit guard covers the
 * case where a subscription arrives preselected (selling_plan URL param) and
 * the shopper heads straight for add to cart without touching the radios.
 *
 * The widget is app-injected, so all wiring is event delegation on document;
 * it works no matter when (or how often) Appstle renders. Only trusted events
 * open the dialog, so Appstle's own programmatic clicks never trigger it.
 * Browsers without <dialog> support skip the flow entirely rather than
 * breaking purchasing.
 */
(function () {
  'use strict';

  if (typeof HTMLDialogElement === 'undefined') return;

  var COPY = {
    title: 'Before you subscribe',
    intro:
      'Subscription pricing is discounted in exchange for a minimum commitment. Please review the terms below before confirming your subscription.',
    points: [
      'Your subscription runs for a minimum of three (3) orders. You will receive, and be billed for, each of your first three scheduled orders.',
      'Your subscription cannot be cancelled while the minimum commitment is in place. Once your third order has been processed, you can cancel, pause, or adjust your subscription at any time through your account.',
      'Because subscription pricing is discounted, requests to cancel before the minimum commitment has been met will not be actioned, except in genuinely exceptional circumstances assessed at our discretion.'
    ],
    checkbox: 'I understand and agree to the minimum three-order commitment described above.',
    confirm: 'Agree and subscribe',
    decline: 'No thanks, keep one-time purchase'
  };

  var dialog = null;
  var checkboxEl = null;
  var confirmBtn = null;
  var activeWidget = null;
  var pendingForm = null;
  var agreed = false;

  function isPlanRadio(el) {
    return !!el && el.tagName === 'INPUT' && el.type === 'radio' && el.name === 'selling_plan';
  }

  function oneTimeRadioFor(widget) {
    if (!widget) return null;
    var radios = widget.querySelectorAll('input[type="radio"][name="selling_plan"]');
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].value === '') return radios[i];
    }
    return null;
  }

  function build() {
    dialog = document.createElement('dialog');
    dialog.className = 'ak-subterms';
    dialog.setAttribute('aria-labelledby', 'ak-subterms-title');

    var points = COPY.points
      .map(function (p) {
        return '<li class="ak-subterms__point">' + p + '</li>';
      })
      .join('');

    dialog.innerHTML =
      '<div class="ak-subterms__inner">' +
      '<h2 class="ak-subterms__title" id="ak-subterms-title">' + COPY.title + '</h2>' +
      '<p class="ak-subterms__intro">' + COPY.intro + '</p>' +
      '<ul class="ak-subterms__points">' + points + '</ul>' +
      '<label class="ak-subterms__agree">' +
      '<input type="checkbox" class="ak-subterms__checkbox">' +
      '<span>' + COPY.checkbox + '</span>' +
      '</label>' +
      '<div class="ak-subterms__actions">' +
      '<button type="button" class="ak-subterms__confirm" disabled>' + COPY.confirm + '</button>' +
      '<button type="button" class="ak-subterms__decline">' + COPY.decline + '</button>' +
      '</div>' +
      '</div>';

    checkboxEl = dialog.querySelector('.ak-subterms__checkbox');
    confirmBtn = dialog.querySelector('.ak-subterms__confirm');

    checkboxEl.addEventListener('change', function () {
      confirmBtn.disabled = !checkboxEl.checked;
    });

    confirmBtn.addEventListener('click', agree);
    dialog.querySelector('.ak-subterms__decline').addEventListener('click', decline);

    // Esc key: native cancel; treat as a decline so state stays consistent.
    dialog.addEventListener('cancel', function (e) {
      e.preventDefault();
      decline();
    });

    // Backdrop click: dismiss without agreeing.
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) decline();
    });

    document.body.appendChild(dialog);
  }

  function open(widget, form) {
    if (!dialog) build();
    if (dialog.open) return;
    activeWidget = widget;
    pendingForm = form || null;
    checkboxEl.checked = false;
    confirmBtn.disabled = true;
    dialog.showModal();
  }

  function closeDialog() {
    activeWidget = null;
    pendingForm = null;
    if (dialog && dialog.open) dialog.close();
  }

  function agree() {
    var form = pendingForm;
    agreed = true;
    closeDialog();
    if (form) {
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    }
  }

  function decline() {
    var widget = activeWidget;
    agreed = false;
    closeDialog();
    var oneTime = oneTimeRadioFor(widget);
    // A real click lets Appstle run its own handlers (collapse the dropdown,
    // reswap prices, move the active-card styling).
    if (oneTime && !oneTime.checked) oneTime.click();
  }

  document.addEventListener(
    'change',
    function (e) {
      var t = e.target;
      if (!isPlanRadio(t) || !t.checked) return;
      var widget = t.closest('.appstle_sub_widget');
      if (!widget) return;
      if (t.value === '') {
        // Back on one-time purchase: any prior agreement no longer stands.
        agreed = false;
        return;
      }
      if (!e.isTrusted) return;
      agreed = false;
      open(widget, null);
    },
    true
  );

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement) || agreed) return;
      var checked = form.querySelector('.appstle_sub_widget input[type="radio"][name="selling_plan"]:checked');
      if (!checked || checked.value === '') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      open(checked.closest('.appstle_sub_widget'), form);
    },
    true
  );
})();
